import React, { useState, useEffect } from 'react';
import {
  Input, Row, Col, Typography,
} from '@revfluence/fresh';
import { validateDeepLink } from '@frontend/applications/AffiliatesApp/components/MembersWizard/utils/validateDeepLinkUrls';
import { GetOfferById_offer } from '@frontend/applications/AffiliatesApp/queries/types/GetOfferById';

const { Text } = Typography;

interface BulkViewProps {
  onInputChange: (deepLinkLabel: string, deepLinkUrl: string) => void;
  offerData?: GetOfferById_offer;
}

const BulkView: React.FC<BulkViewProps> = ({ onInputChange, offerData }) => {
  const [deepLinkLabel, setDeepLinkLabel] = useState('');
  const [deepLinkUrl, setDeepLinkUrl] = useState('');
  const offerLink = offerData?.links[0];
  const [labelError, setLabelError] = useState<string | null>(null);
  const [linkValidation, setLinkValidation] = useState<{ error: string | null; warning: string | null }>({
    error: null,
    warning: null,
  });

  // Fix for input interaction issues
  useEffect(() => {
    // FIX: Remove pointer-events: none from body
    const body = document.body;
    if (body.style.pointerEvents === 'none') {
      body.style.pointerEvents = 'auto';
    }

    // FIX: Remove problematic menus that steal focus
    const removeProblematicMenus = () => {
      const menuItems = document.querySelectorAll('[role="menuitem"], [role="menu"], [data-radix-collection-item]');
      menuItems.forEach((item) => {
        const parent = item.closest('[role="menu"], [data-radix-popper-content-wrapper]');
        if (parent) {
          (parent as HTMLElement).style.display = 'none';
          (parent as HTMLElement).style.visibility = 'hidden';
        }
        item.setAttribute('tabindex', '-1');
        (item as HTMLElement).style.pointerEvents = 'none';
      });

      const openMenus = document.querySelectorAll('[data-state="open"], [aria-expanded="true"]');
      openMenus.forEach((menu) => {
        if (menu.getAttribute('role') === 'menu' || menu.getAttribute('role') === 'listbox') {
          menu.setAttribute('data-state', 'closed');
          menu.setAttribute('aria-expanded', 'false');
          (menu as HTMLElement).style.display = 'none';
        }
      });
    };

    // Run immediately and with delays
    removeProblematicMenus();
    setTimeout(removeProblematicMenus, 100);
    setTimeout(removeProblematicMenus, 300);

    // Prevent menuitems from stealing focus
    let focusAttempts = 0;
    const interceptFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target && target.getAttribute('role') === 'menuitem') {
        focusAttempts++;
        if (focusAttempts > 3) {
          target.remove();
          focusAttempts = 0;
          return;
        }
        e.preventDefault();
        e.stopImmediatePropagation();
        return false;
      }
    };

    document.addEventListener('focus', interceptFocus, true);

    return () => {
      document.removeEventListener('focus', interceptFocus, true);
    };
  }, []);

  const handleValidateDeepLink = (value: string) => {
    const validation = validateDeepLink(value, offerLink.url, offerLink.creatorDeeplink, offerLink.domains);
    setLinkValidation({
      error: validation.error,
      warning: validation.warning,
    });
  };

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const label = e.target.value;
    setDeepLinkLabel(label);
    setLabelError(!label ? 'Label is required' : null);
    onInputChange(label, deepLinkUrl);
  };

  const handleUrlChange = (url: string) => {
    setDeepLinkUrl(url);
    handleValidateDeepLink(url);
    onInputChange(deepLinkLabel, url);
  };

  return (
    <>
      <div>
        <Row className="mt-4" gutter={24}>
          <Col span={24}>
            <Text>Enter label for the link</Text>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col span={24}>
            <Input
              placeholder="Label"
              style={{ width: '100%' }}
              value={deepLinkLabel}
              onChange={handleLabelChange}
              status={labelError ? 'error' : ''}
              type="text"
              onKeyDown={(e) => {
                if (e.key === ' ') {
                  // Fix: Force the space to be added since onChange isn't getting it
                  e.preventDefault();
                  const input = e.currentTarget as HTMLInputElement;
                  const start = input.selectionStart || 0;
                  const end = input.selectionEnd || 0;
                  const currentValue = deepLinkLabel;
                  const newValue = `${currentValue.substring(0, start)} ${currentValue.substring(end)}`;

                  // Update state directly
                  setDeepLinkLabel(newValue);
                  setLabelError(!newValue ? 'Label is required' : null);
                  onInputChange(newValue, deepLinkUrl);

                  // Set cursor position after React re-renders
                  setTimeout(() => {
                    input.setSelectionRange(start + 1, start + 1);
                  }, 0);
                }
              }}
            />
            {labelError && (
              <Text type="danger" className="text-red-500">
                {labelError}
              </Text>
            )}
          </Col>
        </Row>
      </div>
      <div>
        <Row className="mt-4">
          <Col>
            <Text>Redirect Links</Text>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col span={24}>
            <Input
              placeholder="Enter deep link URL"
              style={{ width: '100%' }}
              value={deepLinkUrl}
              onChange={(e) => handleUrlChange(e.target.value)}
              status={linkValidation.error ? 'error' : linkValidation.warning ? 'warning' : ''}
            />
            {linkValidation.error && (
              <Text type="danger" className="text-red-500">
                {linkValidation.error}
              </Text>
            )}
            {!linkValidation.error && linkValidation.warning && (
              <Text type="warning" className="text-yellow-500">
                {linkValidation.warning}
              </Text>
            )}
          </Col>
        </Row>
      </div>
    </>
  );
};

export default BulkView;
